// utils/photoEditor.js - Utility functions for photo editing
import { dataURLToBlob } from './helpers';

// Load image into canvases
export const loadCanvases = async (
  previewCanvas,
  originalSizeCanvas,
  parentElement = null,
  fileURL
) => {
  const previewCtx = previewCanvas.getContext('2d');
  const originalCtx = originalSizeCanvas.getContext('2d');
  
  if (!previewCtx || !originalCtx) {
    console.error('Canvas context is null');
    return;
  }
  
  previewCtx.imageSmoothingEnabled = false;
  originalCtx.imageSmoothingEnabled = false;
  
  const img = new Image();
  img.crossOrigin = "anonymous";  // Add this line to enable CORS
  img.src = fileURL;
  
  return new Promise((resolve, reject) => {
    img.onload = () => {
      try {
        // Calculate scale for preview canvas based on parent element
        let scale = 1;
        if (parentElement) {
          scale = Math.min(
            parentElement.clientWidth / img.width,
            parentElement.clientHeight / img.height
          );
        }
        
        // Set dimensions for preview canvas
        const previewWidth = img.width * scale;
        const previewHeight = img.height * scale;
        previewCanvas.width = previewWidth;
        previewCanvas.height = previewHeight;
        
        // Draw image on preview canvas
        previewCtx.clearRect(0, 0, previewWidth, previewHeight);
        previewCtx.drawImage(img, 0, 0, previewWidth, previewHeight);
        
        // Set dimensions for original size canvas
        originalSizeCanvas.width = img.width;
        originalSizeCanvas.height = img.height;
        
        // Draw image on original size canvas
        originalCtx.clearRect(0, 0, img.width, img.height);
        originalCtx.drawImage(img, 0, 0, img.width, img.height);
        
        resolve(true);
      } catch (error) {
        console.error('Error loading image:', error);
        reject(error);
      }
    };
    
    img.onerror = (error) => {
      console.error('Error loading image:', error);
      reject(error);
    };
  });
};

// Apply color adjustments to canvases
export const applyFilters = async (
  canvases,
  originalFileURL,
  values
) => {
  try {
    for (const canvas of canvases) {
      if (!canvas) continue;
      
      const context = canvas.getContext('2d');
      if (!context) continue;
      
      // Create filter string based on adjustment values
      const filterString = `brightness(${values.brightness}%) contrast(${values.contrast}%) saturate(${values.saturation}%) blur(${values.blur}px) invert(${values.invert ? 100 : 0}%)`;
      
      context.imageSmoothingEnabled = false;
      context.filter = filterString;
      
      // Load original image
      const image = new Image();
      image.crossOrigin = "anonymous";  // Add this line to enable CORS
      image.src = originalFileURL;
      
      await new Promise((resolve, reject) => {
        image.onload = () => {
          try {
            // Clear canvas and draw image with filters applied
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.save();
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            context.restore();
            resolve(true);
          } catch (error) {
            reject(error);
          }
        };
        
        image.onerror = (error) => {
          reject(error);
        };
      });
    }
  } catch (error) {
    console.error('Error applying filters:', error);
    throw error;
  }
};

// Crop canvas to specified ratio
export const cropCanvas = (
  canvas,
  widthRatio,
  heightRatio
) => {
  const context = canvas.getContext('2d');
  if (!context || !canvas) return;
  
  const aspectRatio = widthRatio / heightRatio;
  
  // Get current image data
  const img = new Image();
  img.crossOrigin = "anonymous";  // Add this line to enable CORS
  img.src = canvas.toDataURL();
  
  img.onload = () => {
    const sourceWidth = img.width;
    const sourceHeight = img.height;
    
    let sourceX = 0;
    let sourceY = 0;
    
    // Calculate crop coordinates based on aspect ratio
    if (sourceWidth / sourceHeight > aspectRatio) {
      // Image is wider than target ratio
      const newWidth = sourceHeight * aspectRatio;
      sourceX = (sourceWidth - newWidth) / 2;
    } else {
      // Image is taller than target ratio
      const newHeight = sourceWidth / aspectRatio;
      sourceY = (sourceHeight - newHeight) / 2;
    }
    
    const newWidth = sourceWidth - 2 * sourceX;
    const newHeight = sourceHeight - 2 * sourceY;
    
    // Clear canvas and draw cropped image
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set new canvas dimensions
    canvas.width = newWidth;
    canvas.height = newHeight;
    
    // Draw cropped image
    context.drawImage(
      img,
      sourceX,
      sourceY,
      newWidth,
      newHeight,
      0,
      0,
      newWidth,
      newHeight
    );
  };
};

// Rotate canvas by specified angle
export const rotateCanvas = (canvas, angle) => {
  const context = canvas.getContext('2d');
  if (!context || !canvas) return;
  
  // Get current image data
  const image = new Image();
  image.crossOrigin = "anonymous";  // Add this line to enable CORS
  image.src = canvas.toDataURL();
  
  image.onload = () => {
    // Convert angle to radians
    const radians = (angle * Math.PI) / 180;
    const sin = Math.sin(radians);
    const cos = Math.cos(radians);
    
    // Calculate new dimensions after rotation
    const newWidth = Math.abs(image.width * cos) + Math.abs(image.height * sin);
    const newHeight = Math.abs(image.width * sin) + Math.abs(image.height * cos);
    
    // Clear canvas and set new dimensions
    context.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = newWidth;
    canvas.height = newHeight;
    
    // Translate to center, rotate, and draw
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate(radians);
    context.drawImage(
      image,
      -image.width / 2,
      -image.height / 2,
      image.width,
      image.height
    );
    
    // Reset transformation
    context.setTransform(1, 0, 0, 1, 0, 0);
  };
};

// Flip canvas horizontally or vertically
export const flipCanvas = (canvas, direction) => {
  const context = canvas.getContext('2d');
  if (!context || !canvas) return;
  
  // Get current image data
  const image = new Image();
  image.crossOrigin = "anonymous";  // Add this line to enable CORS
  image.src = canvas.toDataURL();
  
  image.onload = () => {
    // Clear canvas
    context.clearRect(0, 0, canvas.width, canvas.height);
    
    // Save context state
    context.save();
    
    // Apply flip transformation
    if (direction === 'horizontal') {
      context.translate(canvas.width, 0);
      context.scale(-1, 1);
    } else if (direction === 'vertical') {
      context.translate(0, canvas.height);
      context.scale(1, -1);
    }
    
    // Draw flipped image
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    
    // Restore context state
    context.restore();
  };
};

// Export canvas to blob
export const exportCanvasToBlob = (canvas, mimeType = 'image/jpeg') => {
  if (!canvas) return null;
  
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, mimeType);
  });
};

// Export canvas to data URL
export const exportCanvasToDataURL = (canvas, mimeType = 'image/jpeg') => {
  if (!canvas) return null;
  return canvas.toDataURL(mimeType);
};
